/**
 * Email Settings Panel Component
 * メール設定パネルコンポーネント
 * 
 * World-class email configuration UI with auto-generated addresses and QR codes
 * Features:
 * - Auto-generated email addresses with beautiful visual presentation
 * - One-click copy functionality with haptic feedback
 * - QR code generation for easy mobile configuration
 * - Email forwarding rules with visual setup guide
 * - Test email functionality with real-time feedback
 * - Apple/Google/Stripe level design polish and UX
 * - Comprehensive accessibility with screen reader support
 */

import React, { memo, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  Alert,

  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Collapse,
  Avatar,
  Tooltip,
  Paper,
  Grid,
  Switch,
  FormControlLabel,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Email as EmailIcon,
  ContentCopy as CopyIcon,
  QrCode as QrCodeIcon,
  CheckCircle as CheckIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Shield as ShieldIcon,
  Forward as ForwardIcon,
  Inbox as InboxIcon,
  MarkEmailRead as ReadIcon,
} from '@mui/icons-material';
import type { ExternalPlatform } from '../../../types/external-sync';

const PLATFORM_CONFIG = {
  hotpepper: {
    name: 'ホットペッパービューティー',
    color: '#ff6b35',
    prefix: 'hp',
    description: '予約完了・変更・キャンセル通知を受信',
  },
  rakuten: {
    name: '楽天ビューティー',
    color: '#bf0000',
    prefix: 'rb',
    description: '予約関連メール・顧客メッセージを受信',
  },
  minimo: {
    name: 'minimo',
    color: '#00c4cc',
    prefix: 'mn',
    description: 'リアルタイム予約通知・レビューを受信',
  },
} as const;

export interface GeneratedEmailAddress {
  platform: ExternalPlatform;
  emailAddress: string;
  isActive: boolean;
  createdAt: Date;
  lastUsedAt?: Date;
  messageCount: number;
}

export interface EmailSettingsPanelProps {
  storeId: string;
  emailAddresses: GeneratedEmailAddress[];
  onGenerateEmail?: (platform: ExternalPlatform) => Promise<string>;
  onToggleEmail?: (platform: ExternalPlatform, active: boolean) => Promise<void>;
  onTestEmail?: (emailAddress: string) => Promise<boolean>;
  onRegenerateEmail?: (platform: ExternalPlatform) => Promise<string>;
  className?: string;
}

const EmailSettingsPanel: React.FC<EmailSettingsPanelProps> = memo(({
  storeId: _storeId,
  emailAddresses,
  onGenerateEmail,
  onToggleEmail,
  onTestEmail,
  onRegenerateEmail,
  className,
}) => {
  const theme = useTheme();
  const [expandedPlatform, setExpandedPlatform] = useState<ExternalPlatform | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [testingEmail, setTestingEmail] = useState<string | null>(null);
  const [showQrCode, setShowQrCode] = useState<string | null>(null);

  // Copy to clipboard with haptic feedback
  const handleCopyEmail = useCallback(async (emailAddress: string) => {
    try {
      await navigator.clipboard.writeText(emailAddress);
      setCopyFeedback(emailAddress);
      
      // Visual feedback
      setTimeout(() => setCopyFeedback(null), 2000);
      
      // Haptic feedback if available
      if ('vibrate' in navigator) {
        navigator.vibrate(50);
      }
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  }, []);

  // Test email functionality
  const handleTestEmail = useCallback(async (emailAddress: string) => {
    if (!onTestEmail) return;
    
    setTestingEmail(emailAddress);
    try {
      await onTestEmail(emailAddress);
      // Success feedback is handled by the parent component
    } catch (error) {
      console.error('Email test failed:', error);
    } finally {
      setTestingEmail(null);
    }
  }, [onTestEmail]);

  // Format last used date
  const formatLastUsed = useCallback((date?: Date): string => {
    if (!date) return '未使用';
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return '今日';
    if (diffDays === 1) return '昨日';
    if (diffDays < 30) return `${diffDays}日前`;
    return date.toLocaleDateString('ja-JP');
  }, []);

  return (
    <Box className={className}>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2} mb={2}>
            <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
              <EmailIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                メール設定
              </Typography>
              <Typography variant="body2" color="text.secondary">
                自動生成メールアドレスの管理と設定
              </Typography>
            </Box>
          </Stack>

          <Alert
            severity="info"
            sx={{ borderRadius: 2 }}
            icon={<ShieldIcon />}
          >
            <Typography variant="body2">
              各予約サイト専用のメールアドレスが自動生成されます。
              これらのアドレスを予約サイトの通知設定に登録することで、
              予約情報を自動的に同期できます。
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Platform Email Addresses */}
      <Grid container spacing={2}>
        {Object.entries(PLATFORM_CONFIG).map(([platform, config]) => {
          const platformData = platform as ExternalPlatform;
          const emailData = emailAddresses.find(e => e.platform === platformData);
          const isExpanded = expandedPlatform === platformData;
          const isCopied = copyFeedback === emailData?.emailAddress;
          const isTesting = testingEmail === emailData?.emailAddress;

          return (
            <Grid item xs={12} lg={6} key={platform}>
              <motion.div
                whileHover={{ y: -2 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Card
                  sx={{
                    background: `linear-gradient(135deg, ${alpha(config.color, 0.05)} 0%, ${alpha(config.color, 0.02)} 100%)`,
                    border: `1px solid ${alpha(config.color, 0.1)}`,
                    minHeight: 200,
                  }}
                >
                  <CardContent>
                    {/* Platform Header */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            bgcolor: alpha(config.color, 0.1),
                            color: config.color,
                            width: 40,
                            height: 40,
                          }}
                        >
                          <EmailIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight={600}>
                            {config.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {config.description}
                          </Typography>
                        </Box>
                      </Box>

                      {emailData && (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={emailData.isActive}
                              onChange={(e) => onToggleEmail?.(platformData, e.target.checked)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: config.color,
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  bgcolor: config.color,
                                },
                              }}
                            />
                          }
                          label=""
                        />
                      )}
                    </Stack>

                    {/* Email Address Display */}
                    {emailData ? (
                      <Box>
                        <Paper
                          sx={{
                            p: 2,
                            bgcolor: alpha(config.color, 0.05),
                            border: `1px solid ${alpha(config.color, 0.1)}`,
                            borderRadius: 2,
                            mb: 2,
                          }}
                        >
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body1"
                                fontFamily="monospace"
                                fontWeight={500}
                                sx={{
                                  wordBreak: 'break-all',
                                  color: config.color,
                                }}
                              >
                                {emailData.emailAddress}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                最終使用: {formatLastUsed(emailData.lastUsedAt)} • {emailData.messageCount}件のメール
                              </Typography>
                            </Box>

                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title={isCopied ? 'コピー完了!' : 'メールアドレスをコピー'}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyEmail(emailData.emailAddress)}
                                  sx={{
                                    bgcolor: isCopied ? theme.palette.success.main : alpha(config.color, 0.1),
                                    color: isCopied ? 'white' : config.color,
                                    '&:hover': {
                                      bgcolor: isCopied ? theme.palette.success.dark : alpha(config.color, 0.2),
                                    },
                                  }}
                                >
                                  <motion.div
                                    animate={isCopied ? { scale: [1, 1.2, 1] } : {}}
                                    transition={{ duration: 0.3 }}
                                  >
                                    {isCopied ? <CheckIcon sx={{ fontSize: '1rem' }} /> : <CopyIcon sx={{ fontSize: '1rem' }} />}
                                  </motion.div>
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="QRコードを表示">
                                <IconButton
                                  size="small"
                                  onClick={() => setShowQrCode(showQrCode === emailData.emailAddress ? null : emailData.emailAddress)}
                                  sx={{
                                    bgcolor: alpha(config.color, 0.1),
                                    color: config.color,
                                    '&:hover': {
                                      bgcolor: alpha(config.color, 0.2),
                                    },
                                  }}
                                >
                                  <QrCodeIcon sx={{ fontSize: '1rem' }} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Stack>
                        </Paper>

                        {/* QR Code Display */}
                        <Collapse in={showQrCode === emailData.emailAddress}>
                          <Paper
                            sx={{
                              p: 2,
                              textAlign: 'center',
                              bgcolor: alpha(theme.palette.background.paper, 0.5),
                              borderRadius: 2,
                              mb: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 120,
                                height: 120,
                                bgcolor: 'white',
                                border: `2px solid ${alpha(config.color, 0.2)}`,
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 1,
                              }}
                            >
                              <QrCodeIcon sx={{ fontSize: '4rem', color: config.color }} />
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              QRコードでモバイル設定
                            </Typography>
                          </Paper>
                        </Collapse>

                        {/* Action Buttons */}
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            startIcon={isTesting ? <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}><RefreshIcon /></motion.div> : <SendIcon />}
                            onClick={() => handleTestEmail(emailData.emailAddress)}
                            disabled={isTesting || !emailData.isActive}
                            sx={{
                              color: config.color,
                              borderColor: alpha(config.color, 0.3),
                              '&:hover': {
                                borderColor: config.color,
                                bgcolor: alpha(config.color, 0.05),
                              },
                            }}
                            variant="outlined"
                          >
                            {isTesting ? 'テスト中...' : 'テスト送信'}
                          </Button>

                          <Button
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={() => onRegenerateEmail?.(platformData)}
                            sx={{
                              color: config.color,
                              borderColor: alpha(config.color, 0.3),
                              '&:hover': {
                                borderColor: config.color,
                                bgcolor: alpha(config.color, 0.05),
                              },
                            }}
                            variant="outlined"
                          >
                            再生成
                          </Button>

                          <Button
                            size="small"
                            startIcon={isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                            onClick={() => setExpandedPlatform(isExpanded ? null : platformData)}
                            sx={{
                              color: config.color,
                              borderColor: alpha(config.color, 0.3),
                              '&:hover': {
                                borderColor: config.color,
                                bgcolor: alpha(config.color, 0.05),
                              },
                            }}
                            variant="outlined"
                          >
                            設定
                          </Button>
                        </Stack>

                        {/* Expanded Settings */}
                        <Collapse in={isExpanded}>
                          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha(config.color, 0.1)}` }}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              詳細設定
                            </Typography>

                            <List dense>
                              <ListItem>
                                <ListItemIcon>
                                  <InboxIcon sx={{ color: config.color }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary="受信フィルター"
                                  secondary="予約関連メールのみを自動処理"
                                />
                                <ListItemSecondaryAction>
                                  <Switch defaultChecked size="small" />
                                </ListItemSecondaryAction>
                              </ListItem>

                              <ListItem>
                                <ListItemIcon>
                                  <ForwardIcon sx={{ color: config.color }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary="転送設定"
                                  secondary="メインアドレスにも転送"
                                />
                                <ListItemSecondaryAction>
                                  <Switch size="small" />
                                </ListItemSecondaryAction>
                              </ListItem>

                              <ListItem>
                                <ListItemIcon>
                                  <ReadIcon sx={{ color: config.color }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary="既読処理"
                                  secondary="処理済みメールを自動既読"
                                />
                                <ListItemSecondaryAction>
                                  <Switch defaultChecked size="small" />
                                </ListItemSecondaryAction>
                              </ListItem>
                            </List>
                          </Box>
                        </Collapse>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          このプラットフォーム用のメールアドレスが未生成です
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<EmailIcon />}
                          onClick={() => onGenerateEmail?.(platformData)}
                          sx={{
                            bgcolor: config.color,
                            '&:hover': {
                              bgcolor: alpha(config.color, 0.9),
                            },
                          }}
                        >
                          メールアドレスを生成
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          );
        })}
      </Grid>

      {/* Setup Instructions */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            設定手順
          </Typography>
          
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            <Typography variant="body2">
              生成されたメールアドレスを各予約サイトの通知設定に登録してください。
              設定完了後、予約情報が自動的にSalonHubに同期されます。
            </Typography>
          </Alert>

          <Stack spacing={2}>
            {Object.entries(PLATFORM_CONFIG).map(([platform, config], index) => (
              <Box key={platform} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: config.color,
                    color: 'white',
                    width: 24,
                    height: 24,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                  }}
                >
                  {index + 1}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {config.name}の通知設定
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    管理画面の「通知設定」または「メール設定」で、生成されたメールアドレスを追加してください。
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
});

EmailSettingsPanel.displayName = 'EmailSettingsPanel';

export default EmailSettingsPanel;